import React from "react";
import { Link } from "react-router";
import { motion } from "motion/react";

export interface LogoProps {
  /** Variant of the logo to display */
  variant?: "full" | "icon" | "text" | "svg";
  /** Size preset */
  size?: "sm" | "md" | "lg" | "xl";
  /** Color theme */
  theme?: "default" | "light" | "late-night";
  /** Whether to show tagline below logo (defaults to true) */
  showTagline?: boolean;
  /** Custom tagline text */
  taglineText?: string;
  /** Whether logo links to home page */
  withLink?: boolean;
  /** Extra CSS classes for container */
  className?: string;
  /** Force rendering as SVG img element (/logo.svg) */
  useSvg?: boolean;
}

export const Logo: React.FC<LogoProps> = ({
  variant = "full",
  size = "md",
  theme = "default",
  showTagline = true,
  taglineText = "Someone to talk to, anytime",
  withLink = true,
  className = "",
  useSvg = false,
}) => {
  // If variant === "svg" or useSvg === true, render direct SVG image logo
  if (variant === "svg" || useSvg) {
    const svgHeightClasses = {
      sm: "h-8",
      md: "h-10 sm:h-11",
      lg: "h-12 sm:h-14",
      xl: "h-16 sm:h-20",
    };

    const imgContent = (
      <img
        src="/logo.svg"
        alt="FeelTalk - Someone to talk to, anytime"
        className={`${svgHeightClasses[size]} w-auto object-contain shrink-0 ${className}`}
      />
    );

    if (withLink) {
      return (
        <Link to="/" className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-2xl">
          {imgContent}
        </Link>
      );
    }
    return imgContent;
  }

  // Size mapping for standard React component representation
  const sizeClasses = {
    sm: {
      badge: "w-8 h-8 rounded-xl",
      iconSvg: "w-4 h-4",
      text: "text-lg font-extrabold tracking-tight",
      tagline: "text-[9px] sm:text-[10px]",
      gap: "gap-2",
    },
    md: {
      badge: "w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl",
      iconSvg: "w-4.5 h-4.5 sm:w-5 sm:h-5",
      text: "text-xl sm:text-2xl font-black tracking-tight",
      tagline: "text-[10px] sm:text-xs",
      gap: "gap-2.5",
    },
    lg: {
      badge: "w-12 h-12 rounded-2xl",
      iconSvg: "w-6 h-6",
      text: "text-2xl sm:text-3xl font-black tracking-tight",
      tagline: "text-xs sm:text-sm",
      gap: "gap-3",
    },
    xl: {
      badge: "w-16 h-16 rounded-3xl",
      iconSvg: "w-8 h-8",
      text: "text-3xl sm:text-4xl font-black tracking-tight",
      tagline: "text-sm sm:text-base",
      gap: "gap-4",
    },
  };

  const currentSize = sizeClasses[size];

  // Theme mapping for typography
  let textThemeClass = "bg-gradient-to-r from-purple-600 via-pink-600 to-rose-500 bg-clip-text text-transparent";
  let taglineThemeClass = "text-purple-600/75 font-medium";
  let badgeClass = "bg-gradient-to-tr from-purple-600 via-pink-500 to-rose-500 shadow-md shadow-purple-500/25 text-white";

  if (theme === "light") {
    textThemeClass = "text-white drop-shadow-sm";
    taglineThemeClass = "text-purple-200/90 font-normal";
    badgeClass = "bg-white/20 backdrop-blur-md text-white border border-white/20 shadow-lg shadow-black/10";
  } else if (theme === "late-night") {
    textThemeClass = "text-white";
    taglineThemeClass = "text-indigo-300/90 font-normal";
    badgeClass = "bg-gradient-to-tr from-indigo-600 via-purple-600 to-pink-500 text-white shadow-md shadow-indigo-500/25";
  }

  const LogoIcon = () => (
    <motion.div
      whileHover={{ scale: 1.08, rotate: [0, -4, 4, 0] }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className={`relative flex items-center justify-center shrink-0 ${currentSize.badge} ${badgeClass}`}
    >
      <svg
        className={currentSize.iconSvg}
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        <circle cx="17.5" cy="6.5" r="1.5" fill="rgba(255,255,255,0.75)" />
      </svg>
    </motion.div>
  );

  const LogoTypography = () => (
    <div className="flex flex-col justify-center select-none shrink-0">
      <span className={`${currentSize.text} ${textThemeClass} leading-none flex items-center`}>
        Feel<span className={theme === "light" ? "text-pink-300" : "text-pink-600"}>Talk</span>
      </span>
      {showTagline && (
        <span className={`${currentSize.tagline} ${taglineThemeClass} mt-0.5 leading-tight tracking-tight whitespace-nowrap hidden sm:block`}>
          {taglineText}
        </span>
      )}
    </div>
  );

  const content = (
    <div className={`inline-flex items-center ${currentSize.gap}`}>
      {(variant === "full" || variant === "icon") && <LogoIcon />}
      {(variant === "full" || variant === "text") && <LogoTypography />}
    </div>
  );

  if (withLink) {
    return (
      <Link to="/" className={`inline-flex items-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-2xl ${className}`}>
        {content}
      </Link>
    );
  }

  return <div className={`inline-flex items-center ${className}`}>{content}</div>;
};

export default Logo;
